import React, { useMemo } from 'react';

import { cssMap } from '@atlaskit/css';
import { Box, Flex } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import {
	PrimaryProductKeys,
	ProductKeys,
	useProductConfigs,
} from '../../common/constants/products';
import { getConnected3PIcons } from '../../common/utils/quick-find/get-connected-3p-icons';
import { useNewlyConnectedProducts } from '../bulk-connect';
import { usePrimaryProduct } from '../store/bootstrap';

const styles = cssMap({
	container: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.050'),
	},
	iconBox: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '16px',
		height: '16px',
		overflow: 'hidden',
		borderRadius: token('radius.small'),
		flexShrink: 0,
	},
});

/**
 * Renders a strip of 3 app icons (1P + 2 connected 3P icons), wiring all
 * bootstrap state, 3P connection state, and GitHub experiment logic internally.
 *
 */
export const DynamicAllAppsIcon = (): React.JSX.Element => {
	const [primaryProduct] = usePrimaryProduct();
	const { getNewlyConnectedProducts } = useNewlyConnectedProducts();
	const productConfigs = useProductConfigs();

	const [ThirdPartyIcon1, ThirdPartyIcon2] = useMemo(() => {
		return getConnected3PIcons(getNewlyConnectedProducts(), productConfigs);
	}, [getNewlyConnectedProducts, productConfigs]);

	const FirstPartyIcon =
		primaryProduct === PrimaryProductKeys.Jira
			? productConfigs[ProductKeys.Confluence]?.Icon
			: productConfigs[ProductKeys.Jira]?.Icon;

	return (
		<Flex role="presentation" aria-hidden="true" xcss={styles.container}>
			{FirstPartyIcon && (
				<Box xcss={styles.iconBox}>
					<FirstPartyIcon size="xxsmall" />
				</Box>
			)}
			<Box xcss={styles.iconBox}>
				<ThirdPartyIcon1 size="xxsmall" />
			</Box>
			<Box xcss={styles.iconBox}>
				<ThirdPartyIcon2 size="xxsmall" />
			</Box>
		</Flex>
	);
};
